import * as React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Collapse,
  Paper,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AddIcon from '@mui/icons-material/Add';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { withTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../utils/languages';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import useCalendarState from './hooks/useCalendarState';
import CalendarSearch from './CalendarSearch';
import { formatWeekRange, getWeekNumber, formatDayDate, formatMonthTitle } from './hooks/dateUtils';
import { weekSpanText } from './hooks/monthUtils';
import { ALL_WEEK_DAYS, FILTER_NONE } from './calendarConstants';
import { deriveTagTextColor } from './calendarColors';
import useCalendarColors from './hooks/useCalendarColors';

// Helper to convert "HH:mm" string to Date object
const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Helper to convert Date object to "HH:mm" string
const dateToTimeString = (date) => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

function CalendarHeader({ t, i18n, hideResourceSwitcher = false, hideCreateButton = false, isShortScreen = false }) {
  const {
    currentView,
    setCurrentView,
    calendarMode,
    setCalendarMode,
    selectedDay,
    currentDate,
    getWeekDates,
    goToNextWeek,
    goToPrevWeek,
    goToToday,
    goToNextDay,
    goToPrevDay,
    goToNextMonth,
    goToPrevMonth,
    monthData,
    switchToDay,
    openScheduleDialog,
    filterPanelOpen,
    toggleFilterPanel,
    navHidden,
    toggleNavHidden,
    filters,
    updateFilters,
    resetFilters,
    getActiveFilterCount,
    employees,
    serviceBays,
    workSchedule,
  } = useCalendarState();
  const { statusEntries, activeTags } = useCalendarColors();

  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  const dateTextRef = React.useRef(null);

  const isDayMode = calendarMode === 'day';
  const isMonthMode = calendarMode === 'month';
  const dayDate = selectedDay || currentDate;

  const weekDates = getWeekDates();
  const weekRangeText = formatWeekRange(weekDates[0], weekDates[weekDates.length - 1]);
  const weekNumber = getWeekNumber(weekDates[0]);
  const dayWeekNumber = getWeekNumber(dayDate);
  const filterCount = getActiveFilterCount();

  // Day mode date text
  const language = normalizeLanguage(i18n?.language);
  const dayDateText = formatDayDate(dayDate, language);
  const monthTitleText = formatMonthTitle(currentDate, language);
  const monthWeeksText = monthData ? weekSpanText(monthData.days) : '';
  // Adaptive navigation handlers
  const handlePrev = isDayMode ? goToPrevDay : isMonthMode ? goToPrevMonth : goToPrevWeek;
  const handleNext = isDayMode ? goToNextDay : isMonthMode ? goToNextMonth : goToNextWeek;

  // Default weekdays: the organization's open days (a week may have a gap, e.g. Wednesday closed)
  const defaultWeekdays = workSchedule.weekdays;

  // Current visible weekdays (filter or default)
  const currentWeekdays = filters.visibleWeekdays ?? defaultWeekdays;

  // Status options for filter, with the organization's colour overrides applied
  const statusOptions = statusEntries.map(([key, config]) => ({
    value: key,
    label: t(config.labelKey),
    color: config.color,
  }));

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Main Header Row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: isShortScreen ? 1 : 2,
          p: isShortScreen ? 1 : 2,
          px: isShortScreen ? 2 : 3,
        }}
      >
        {/* Left: Date Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handlePrev}
            size="small"
            data-testid="calendar-prev-button"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '8px',
              '&:hover': { bgcolor: 'background.default' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {isDayMode ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                ref={dateTextRef}
                onClick={() => setDatePickerOpen(true)}
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  textAlign: 'center',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  px: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 0.15s',
                  userSelect: 'none',
                }}
              >
                {dayDateText}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.2,
                }}
                data-testid="calendar-day-week-number"
              >
                {t('Week')} {dayWeekNumber}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <DatePicker
                  open={datePickerOpen}
                  onOpen={() => setDatePickerOpen(true)}
                  onClose={() => setDatePickerOpen(false)}
                  value={dayDate}
                  onChange={(newDate) => {
                    if (newDate && !isNaN(newDate.getTime())) {
                      switchToDay(newDate);
                    }
                    setDatePickerOpen(false);
                  }}
                  slotProps={{
                    textField: { sx: { display: 'none' } },
                    popper: { anchorEl: dateTextRef.current },
                  }}
                />
              </LocalizationProvider>
            </Box>
          ) : isMonthMode ? (
            <Box sx={{ textAlign: 'center', minWidth: 128 }}>
              <Typography
                data-testid="calendar-month-title"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {monthTitleText}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.2,
                }}
              >
                {monthWeeksText ? `${t('calendar.month.weeks')} ${monthWeeksText}` : '\u00a0'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {weekRangeText}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  lineHeight: 1.2,
                }}
              >
                {t('Week')} {weekNumber}
              </Typography>
            </Box>
          )}

            <IconButton
              onClick={handleNext}
              size="small"
              data-testid="calendar-next-button"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px',
                '&:hover': { bgcolor: 'background.default' },
              }}
            >
              <ChevronRightIcon />
            </IconButton>

            <Button
              onClick={goToToday}
              variant="outlined"
              size="small"
              data-testid="calendar-today-button"
              sx={{
                ml: 1,
                borderRadius: '8px',
                borderColor: 'divider',
                color: 'text.primary',
                fontWeight: 500,
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '&:hover': {
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                },
              }}
            >
              {t('Today')}
            </Button>
        </Box>

        {/* Right: View Switcher and Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Day/Week Mode Toggle */}
          <ToggleButtonGroup
            value={calendarMode}
            exclusive
            onChange={(e, value) => value && setCalendarMode(value)}
            size="small"
            sx={{
              bgcolor: 'background.default',
              borderRadius: '8px',
              p: 0.5,
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: 0.75,
                fontWeight: 500,
                fontSize: '14px',
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                gap: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'background.paper',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': { bgcolor: 'background.paper' },
                },
              },
            }}
          >
            {/* Shortest span first, as in every other calendar the user knows; the default
                (week) sits in the middle. */}
            <ToggleButton value="day" data-testid="calendar-mode-day">
              <CalendarViewDayIcon sx={{ fontSize: 18 }} />
              {t('Day view')}
            </ToggleButton>
            <ToggleButton value="week" data-testid="calendar-mode-week">
              <CalendarViewWeekIcon sx={{ fontSize: 18 }} />
              {t('Week')}
            </ToggleButton>
            <ToggleButton value="month" data-testid="calendar-mode-month">
              <CalendarViewMonthIcon sx={{ fontSize: 18 }} />
              {t('Month')}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Resource View Switcher - hidden when the calendar shows only the own row */}
          {!hideResourceSwitcher && (
            <ToggleButtonGroup
              value={currentView}
              exclusive
              onChange={(e, value) => value && setCurrentView(value)}
              size="small"
              sx={{
                bgcolor: 'background.default',
                borderRadius: '8px',
                p: 0.5,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '6px',
                  px: { xs: 1.5, sm: 2 },
                  py: 0.75,
                  fontWeight: 500,
                  fontSize: '14px',
                  textTransform: 'none',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  '&.Mui-selected': {
                    bgcolor: 'background.paper',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '&:hover': { bgcolor: 'background.paper' },
                  },
                },
              }}
            >
              <ToggleButton value="all">{t('All')}</ToggleButton>
              <ToggleButton value="employee">{t('Employees')}</ToggleButton>
              <ToggleButton value="bay">{t('Service bays')}</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Hide/show the app navigation drawer. Opt-in per browser: the calendar is the one
              page where 260px of menu costs a whole mechanic's column on a wide screen. */}
          <Tooltip title={navHidden ? t('calendar.show_navigation') : t('calendar.hide_navigation')}>
            <IconButton
              size="small"
              onClick={toggleNavHidden}
              data-testid="calendar-toggle-nav-button"
              aria-label={navHidden ? t('calendar.show_navigation') : t('calendar.hide_navigation')}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px',
                '&:hover': { bgcolor: 'background.default' },
              }}
            >
              {navHidden ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Filter Button */}
          <Badge badgeContent={filterCount} color="primary" invisible={filterCount === 0}>
            <Button
              variant={filterPanelOpen ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              size="small"
              onClick={toggleFilterPanel}
              data-testid="calendar-filter-button"
              sx={{
                borderRadius: '8px',
                borderColor: filterPanelOpen ? 'primary.main' : 'divider',
                color: filterPanelOpen ? 'white' : 'text.primary',
                bgcolor: filterPanelOpen ? 'primary.main' : 'transparent',
                fontWeight: 500,
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '&:hover': {
                  borderColor: filterPanelOpen ? 'primary.dark' : 'divider',
                  bgcolor: filterPanelOpen ? 'primary.dark' : 'background.default',
                },
              }}
            >
              {t('Filter')}
            </Button>
          </Badge>

          {/* New Maintenance Button - hidden in the simplified UI */}
          {!hideCreateButton && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              data-testid="calendar-new-maintenance-button"
              onClick={() => openScheduleDialog()}
              sx={{
                bgcolor: 'secondary.main',
                color: 'white',
                borderRadius: '8px',
                px: 2.5,
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
                '&:hover': {
                  bgcolor: 'secondary.light',
                  boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
                },
              }}
            >
              {t('New maintenance')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Panel */}
      <Collapse in={filterPanelOpen}>
        <Paper
          elevation={0}
          sx={{
            mx: 3,
            mb: isShortScreen ? 1 : 2,
            p: isShortScreen ? 1.5 : 2.5,
            borderRadius: '12px',
            bgcolor: 'background.default',
          }}
        >
          {/* Work order search — finding a booking, not filtering the view */}
          <Box sx={{ mb: isShortScreen ? 1.5 : 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
              {t('Search work orders')}
            </Typography>
            <CalendarSearch />
          </Box>

          <Divider sx={{ mb: isShortScreen ? 1.5 : 2.5 }} />

          {/* Month mode: only the employee filter reaches the aggregates; the rest applies
              to the day peek list, and the visible hours/days make no sense at all. */}
          {isMonthMode && (
            <Typography
              data-testid="calendar-month-filter-hint"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: isShortScreen ? 1.5 : 2.5, fontStyle: 'italic' }}
            >
              {t('calendar.month.filterHint')}
            </Typography>
          )}

          {/* Filter Groups Container */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: isShortScreen ? 1.5 : 3,
            }}
          >
            {/* Left Column: Resource Filters */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isShortScreen ? 1.5 : 2.5 }}>
              {/* Resource Filters Row */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {/* Employee Filter */}
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                  <InputLabel>{t('Employees')}</InputLabel>
                  <Select
                    multiple
                    value={filters.employees}
                    onChange={(e) => updateFilters({ employees: e.target.value })}
                    label={t('Employees')}
                    renderValue={(selected) => {
                      const names = selected.map((id) => {
                        if (id === FILTER_NONE) return t('No employee');
                        const emp = employees.find((e) => e.id === id);
                        return emp ? emp.name : id;
                      });
                      return names.join(', ');
                    }}
                  >
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        <Checkbox checked={filters.employees.includes(emp.id)} size="small" />
                        <ListItemText primary={emp.name} />
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value={FILTER_NONE}>
                      <Checkbox checked={filters.employees.includes(FILTER_NONE)} size="small" />
                      <ListItemText primary={t('No employee')} primaryTypographyProps={{ sx: { fontStyle: 'italic', color: 'text.secondary' } }} />
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Service Bay Filter */}
                <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                  <InputLabel>{t('Service bays')}</InputLabel>
                  <Select
                    multiple
                    value={filters.serviceBays}
                    onChange={(e) => updateFilters({ serviceBays: e.target.value })}
                    label={t('Service bays')}
                    renderValue={(selected) => {
                      const names = selected.map((id) => {
                        if (id === FILTER_NONE) return t('No service bay');
                        const bay = serviceBays.find((b) => b.id === id);
                        return bay ? bay.name : id;
                      });
                      return names.join(', ');
                    }}
                  >
                    {serviceBays.map((bay) => (
                      <MenuItem key={bay.id} value={bay.id}>
                        <Checkbox checked={filters.serviceBays.includes(bay.id)} size="small" />
                        <ListItemText primary={bay.name} />
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value={FILTER_NONE}>
                      <Checkbox checked={filters.serviceBays.includes(FILTER_NONE)} size="small" />
                      <ListItemText primary={t('No service bay')} primaryTypographyProps={{ sx: { fontStyle: 'italic', color: 'text.secondary' } }} />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Status Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  {t('Status')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {statusOptions.map((status) => {
                    const isSelected = filters.states.includes(status.value);
                    return (
                      <Chip
                        key={status.value}
                        label={status.label}
                        size="small"
                        onClick={() => {
                          if (isSelected) {
                            updateFilters({ states: filters.states.filter((s) => s !== status.value) });
                          } else {
                            updateFilters({ states: [...filters.states, status.value] });
                          }
                        }}
                        sx={{
                          borderColor: status.color,
                          bgcolor: isSelected ? status.color : 'transparent',
                          color: isSelected ? deriveTagTextColor(status.color) : status.color,
                          border: 1,
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: isSelected ? status.color : `${status.color}20`,
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Colour tag filter (only when the organization has defined tags) */}
              {activeTags.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                    {t('calendar.colorTags.filterTitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {activeTags.map((tag) => {
                      const selectedTags = filters.colorTags || [];
                      const isSelected = selectedTags.includes(tag.id);
                      return (
                        <Chip
                          key={tag.id}
                          data-testid={`calendar-filter-color-tag-${tag.id}`}
                          label={tag.name}
                          size="small"
                          onClick={() =>
                            updateFilters({
                              colorTags: isSelected
                                ? selectedTags.filter((id) => id !== tag.id)
                                : [...selectedTags, tag.id],
                            })
                          }
                          sx={{
                            borderColor: tag.color,
                            bgcolor: isSelected ? tag.color : 'transparent',
                            color: isSelected ? deriveTagTextColor(tag.color) : 'text.primary',
                            border: 1,
                            fontWeight: 500,
                            '&:hover': { bgcolor: isSelected ? tag.color : `${tag.color}20` },
                          }}
                        />
                      );
                    })}
                    {(() => {
                      const selectedTags = filters.colorTags || [];
                      const isSelected = selectedTags.includes(FILTER_NONE);
                      return (
                        <Chip
                          data-testid="calendar-filter-color-tag-none"
                          label={t('calendar.colorTags.filterNone')}
                          size="small"
                          onClick={() =>
                            updateFilters({
                              colorTags: isSelected
                                ? selectedTags.filter((id) => id !== FILTER_NONE)
                                : [...selectedTags, FILTER_NONE],
                            })
                          }
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                            fontStyle: 'italic',
                            fontWeight: 500,
                          }}
                        />
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Right Column: Calendar View Filters (a time axis only exists in week and day mode) */}
            {!isMonthMode && <Box sx={{ display: 'flex', flexDirection: 'column', gap: isShortScreen ? 1.5 : 2.5 }}>
              {/* Weekday Filter - only relevant in week mode */}
              {!isDayMode && <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  {t('Visible days')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {ALL_WEEK_DAYS.map((day) => {
                    const isSelected = currentWeekdays.includes(day.index);
                    const isDefault = defaultWeekdays.includes(day.index);
                    return (
                      <Chip
                        key={day.index}
                        label={t(day.shortKey)}
                        size="small"
                        onClick={() => {
                          let newWeekdays;
                          if (isSelected) {
                            // Don't allow deselecting all days
                            if (currentWeekdays.length <= 1) return;
                            newWeekdays = currentWeekdays.filter((d) => d !== day.index);
                          } else {
                            newWeekdays = [...currentWeekdays, day.index].sort((a, b) => a - b);
                          }
                          // If matches default, set to null
                          const matchesDefault = newWeekdays.length === defaultWeekdays.length &&
                            newWeekdays.every((d, i) => d === defaultWeekdays[i]);
                          updateFilters({ visibleWeekdays: matchesDefault ? null : newWeekdays });
                        }}
                        sx={{
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'white' : 'text.secondary',
                          border: 1,
                          fontWeight: 500,
                          opacity: !isDefault && !isSelected ? 0.7 : 1,
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>}

              {/* Hour Range Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}>
                  {t('Visible hours')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                    <TimePicker
                      value={timeStringToDate(filters.visibleStartHour ?? workSchedule.dayStart ?? '08:00')}
                      onChange={(newValue) => {
                        const timeStr = dateToTimeString(newValue);
                        const defaultStart = workSchedule.dayStart ?? '08:00';
                        updateFilters({ visibleStartHour: timeStr === defaultStart ? null : timeStr });
                      }}
                      ampm={false}
                      slotProps={{
                        textField: { size: 'small', sx: { width: 100 }, placeholder: '08:00' },
                      }}
                    />
                  </LocalizationProvider>
                  <Typography color="text.secondary">—</Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                    <TimePicker
                      value={timeStringToDate(filters.visibleEndHour ?? workSchedule.dayEnd ?? '16:00')}
                      onChange={(newValue) => {
                        const timeStr = dateToTimeString(newValue);
                        const defaultEnd = workSchedule.dayEnd ?? '16:00';
                        updateFilters({ visibleEndHour: timeStr === defaultEnd ? null : timeStr });
                      }}
                      ampm={false}
                      slotProps={{
                        textField: { size: 'small', sx: { width: 100 }, placeholder: '16:00' },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Box>}
          </Box>

          {/* Reset Filters Button */}
          {filterCount > 0 && (
            <Box sx={{ mt: isShortScreen ? 1.5 : 2.5, pt: isShortScreen ? 1 : 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                size="small"
                onClick={resetFilters}
                sx={{
                  textTransform: 'none',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: 'text.secondary',
                }}
              >
                {t('Reset filters')}
              </Button>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
}

export default withTranslation()(CalendarHeader);
